'use client'

import { ComponentProps, useRef, useState } from 'react'

import { Popover } from '@/shared/ui'

type Props = {
  defaultIsOpen?: boolean
  disabled?: boolean
  onClose?: (...args: unknown[]) => void
}

export const usePopover = <T extends HTMLElement>({
  defaultIsOpen = false,
  disabled = false,
  onClose,
}: Props = {}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen)

  const ref = useRef<T>(null)

  const toggleOpen = () => {
    if (disabled) return

    setIsOpen(prevIsOpen => !prevIsOpen)
  }

  const open = () => {
    if (disabled) return

    setIsOpen(true)
  }

  const close = (...args: unknown[]) => {
    setIsOpen(false)
    onClose?.(...args)
  }

  const handleOutsideClick: ComponentProps<
    typeof Popover.Content
  >['onInteractOutside'] = event => {
    if (ref.current?.contains(event.target as Node)) {
      return
    }

    close()
  }

  return {
    isOpen,
    ref,
    open,
    close,
    toggleOpen,
    handleOutsideClick,
  }
}
